import type { BottleSummary, TastingNoteSummary } from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { correctDeparture, saveMyTastingNote } from "../api.js";
import { useCellar, useMayWrite } from "../cellar.js";
import { actionButton, control, fieldLabel, resting } from "./controls.js";

/**
 * The bottles that have gone, and what was written about them.
 *
 * Until this existed a drunk bottle simply vanished from its wine's page: the
 * heading said *0 of 1 left* and there was nothing underneath it. The date, the
 * evening's note and the score were all written faithfully and read by nothing,
 * which broke the promise in `project-scope.md` that a tasting note written
 * five years ago is still findable for a wine no bottle of which remains — the
 * one criterion the whole append-only history exists to serve.
 *
 * **A departure with no date is shown as one.** Thirty-five bottles came in
 * from the sheet as gone on a day nobody had written down; the import refused
 * to invent dates for them and so does this. They say *gone, date unknown*, and
 * the correction below is how a remembered date gets recorded by the person who
 * remembers it.
 *
 * Correcting is not undoing. That the bottle left stands; what changes is what
 * somebody typed about it — see the decision of 2026-09-11 in
 * `project-scope.md`. Undo is still the only way to say it never left, and it
 * lives on the bottle's own controls where it always has.
 *
 * **There is a score here and none on the form that drinks a bottle**, and the
 * difference is deliberate rather than an oversight. `3.10` took the score off
 * that form because the judgement being made as a cork comes out is about the
 * wine — the same 17 typed onto three identical bottles — and left
 * `TastingNote.score` in the model "for a bottle that genuinely differed from
 * its siblings", written by nothing. This is that case and the only place it
 * could be: knowing that one bottle was not like the others is something learnt
 * afterwards, not while pouring. The wine's own standing verdict is still the
 * *Mitt betyg* box at the top of the page, and this is the exception it
 * anticipated. It also has to exist for a duller reason: fourteen imported
 * notes carry a score, and a correction form without the field would wipe them
 * the first time somebody fixed a date.
 */
export function DepartedBottles({
  cellarId,
  bottles,
  notes,
  onCorrected,
}: {
  cellarId: string;
  /** Every bottle of the wine; the ones still owned are filtered out here. */
  bottles: readonly BottleSummary[];
  notes: readonly TastingNoteSummary[];
  onCorrected: () => void;
}) {
  const { t } = useTranslation();
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [writing, setWriting] = useState<string | null>(null);
  const mayWrite = useMayWrite();

  const gone = bottles.filter(
    (bottle) => bottle.status !== "InCellar" && bottle.status !== "Away",
  );
  if (gone.length === 0) return null;

  // Newest first, and the undated last: a date nobody wrote down sorts below
  // every date somebody did, rather than pretending to be the oldest.
  const ordered = [...gone].sort((a, b) => {
    const left = a.departure?.occurredAt ?? "";
    const right = b.departure?.occurredAt ?? "";
    if (left === right) return 0;
    if (left === "") return 1;
    if (right === "") return -1;
    return right.localeCompare(left);
  });

  return (
    <ul className="mt-3">
      {ordered.map((bottle) => {
        // Several, since `9.3`: one per person who was at the table. The
        // reader's own is singled out because it is the only one they may
        // change, and the departure form still edits whichever is theirs.
        const said = notes.filter((row) => row.bottleId === bottle.id);
        const note = said.find((row) => row.mine) ?? null;
        // A bottle that was drunk is the only one there is anything to say
        // about the taste of. The other four ways out of a cellar — given
        // away, sold, lost, broken — left with nobody here tasting them.
        const drunk = bottle.status === "Consumed";
        return (
          <li key={bottle.id} className="border-line border-b py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span>
                {t(`bottleStatus.${bottle.status}`)}
                {bottle.format === "Bottle" ? "" : ` · ${t(`format.${bottle.format}`)}`}
              </span>
              <span className="text-muted flex items-baseline gap-4 text-sm">
                <span>
                  {bottle.departure === null
                    ? t("wine.departed.noDate")
                    : bottle.departure.occurredAt}
                </span>
                {/* A guest reads what was drunk and what was said about it —
                    that is the cellar's history and the pleasant half of it.
                    Correcting a departure is a write. */}
                {mayWrite === true && (
                  <button
                    type="button"
                    className="text-accent underline-offset-4 hover:underline"
                    onClick={() =>
                      setCorrecting(correcting === bottle.id ? null : bottle.id)
                    }
                  >
                    {t("wine.departed.correct")}
                  </button>
                )}
              </span>
            </div>

            {/* What was said about it, by everybody who said anything. The
                score keeps its scale in the label above the field rather than
                here, where the words matter.

                The name is on the row from `9.3` onward, and on every note
                but the reader's own. Your own words need no byline and a
                cellar whose owner is the only writer should not have their
                name stamped down every line it has — but a note written by
                somebody else needs one whether there is one of them or five,
                which is what the first version of this rule got wrong by
                counting notes instead of asking whose it was. A note whose
                author's account has gone carries no name and is still here:
                anonymised rather than deleted, because it was written on
                somebody else's bottle. */}
            {said
              .filter((row) => row.text !== "" || row.score !== null || row.isFaulty)
              .map((row) => (
                <p key={row.id} className="text-muted mt-1 max-w-prose text-sm">
                  {row.isFaulty && (
                    <span className="text-past">{t("wine.faulty")} · </span>
                  )}
                  {row.score !== null && (
                    <span className="font-display text-ink">{row.score} </span>
                  )}
                  {row.text}
                  {!row.mine && (
                    <span className="text-muted">
                      {" — "}
                      {row.authorName ?? t("wine.note.someoneGone")}
                    </span>
                  )}
                </p>
              ))}
            {bottle.departure?.note != null && (
              <p className="text-muted mt-1 max-w-prose text-sm">
                {bottle.departure.note}
              </p>
            )}

            {/* Saying what you thought of a bottle somebody else opened, which
                is the one thing a guest may write. Offered where the reader
                has nothing on this bottle yet; their own note is corrected
                through the form below, which is the owner's door too.

                **Only on a bottle that was drunk**, which is the same fact the
                route goes on: it answers 409 `not_drunk` for anything else,
                and this section lists every way a bottle can leave — given
                away, sold, lost, broken. Without this the button stood on all
                of them and the failure came back as *try again*, which is
                advice that can never work. `project-scope.md` says a feature
                with nothing behind it should be dark rather than broken, and
                there is nothing to say about the taste of a bottle nobody
                here opened. */}
            {mayWrite === false && drunk && writing !== bottle.id && (
              <button
                type="button"
                className="text-accent mt-1 text-sm underline-offset-4 hover:underline"
                onClick={() => setWriting(bottle.id)}
              >
                {t(note === null ? "wine.note.add" : "wine.note.edit")}
              </button>
            )}

            {writing === bottle.id && (
              <MyNoteForm
                cellarId={cellarId}
                bottle={bottle}
                note={note}
                onDone={() => {
                  setWriting(null);
                  onCorrected();
                }}
                onCancel={() => setWriting(null)}
              />
            )}

            {correcting === bottle.id && (
              <CorrectionForm
                cellarId={cellarId}
                bottle={bottle}
                note={note}
                onDone={() => {
                  setCorrecting(null);
                  onCorrected();
                }}
                onCancel={() => setCorrecting(null)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function CorrectionForm({
  cellarId,
  bottle,
  note,
  onDone,
  onCancel,
}: {
  cellarId: string;
  bottle: BottleSummary;
  note: TastingNoteSummary | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const drunk = bottle.status === "Consumed";

  const [occurredAt, setOccurredAt] = useState(bottle.departure?.occurredAt ?? "");
  const [text, setText] = useState((drunk ? note?.text : bottle.departure?.note) ?? "");
  const [score, setScore] = useState(
    note?.score === null || note === undefined || note === null ? "" : String(note.score),
  );
  const [isFaulty, setIsFaulty] = useState(note?.isFaulty ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = /^\d{4}-\d{2}-\d{2}$/.test(occurredAt) && !busy;

  return (
    <form
      className="mt-3 grid gap-4 sm:grid-cols-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        void correctDeparture(cellarId, bottle.id, {
          occurredAt,
          note: text.trim() === "" ? null : text,
          score: drunk && score.trim() !== "" ? Number(score.replace(",", ".")) : null,
          isFaulty: drunk && isFaulty,
        })
          .then(onDone)
          .catch(() => setError(t("wine.departed.saveFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div>
        <label className={fieldLabel} htmlFor={`departed-date-${bottle.id}`}>
          {drunk ? t("wine.departed.drunkOn") : t("wine.departed.leftOn")}
        </label>
        <input
          id={`departed-date-${bottle.id}`}
          type="date"
          value={occurredAt}
          onChange={(event) => setOccurredAt(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      {drunk && (
        <div>
          <label className={fieldLabel} htmlFor={`departed-score-${bottle.id}`}>
            {t("wine.departed.score")}
          </label>
          <input
            id={`departed-score-${bottle.id}`}
            inputMode="decimal"
            value={score}
            onChange={(event) => setScore(event.target.value)}
            className={`${control} ${resting} mt-1`}
          />
        </div>
      )}

      <div className="sm:col-span-3">
        <label className={fieldLabel} htmlFor={`departed-note-${bottle.id}`}>
          {drunk ? t("wine.tastingNote") : t("wine.departed.note")}
        </label>
        <textarea
          id={`departed-note-${bottle.id}`}
          value={text}
          rows={3}
          onChange={(event) => setText(event.target.value)}
          className={`${control} ${resting} mt-1 h-auto py-2`}
        />
      </div>

      {drunk && (
        <label className="col-span-full flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isFaulty}
            onChange={(event) => setIsFaulty(event.target.checked)}
            className="accent-accent"
          />
          {t("wine.faulty")}
        </label>
      )}

      <div className="col-span-full flex items-center gap-4">
        <button type="submit" disabled={!canSubmit} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.departed.save")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
        >
          {t("wine.cancel")}
        </button>
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * Your own note on a bottle somebody else opened.
 *
 * Task `9.3`, and the one form in this application a guest may submit. It asks
 * exactly what the owner's own note asks — what it tasted like, out of the
 * cellar's scale, and whether the bottle was faulty — because it is the same
 * act written by a different person.
 *
 * The date defaults to the day the bottle left rather than to today: that is
 * the evening being written about, and the guest was there for it. It stays
 * editable, because a bottle drunk at a dinner and recorded a week later has
 * two dates and only one of them is the tasting.
 *
 * **Emptying all three removes the note**, which is the rule the correction
 * form beside it already follows: a row saying nothing claims a tasting that
 * produced no opinion.
 */
function MyNoteForm({
  cellarId,
  bottle,
  note,
  onDone,
  onCancel,
}: {
  cellarId: string;
  bottle: BottleSummary;
  note: TastingNoteSummary | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const scale = useCellar()?.tastingScale ?? null;
  const [tastedOn, setTastedOn] = useState(
    note?.tastedOn ??
      bottle.departure?.occurredAt ??
      new Date().toISOString().slice(0, 10),
  );
  const [text, setText] = useState(note?.text ?? "");
  const [score, setScore] = useState(
    note?.score === undefined || note.score === null ? "" : String(note.score),
  );
  const [isFaulty, setIsFaulty] = useState(note?.isFaulty ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const given = score.trim() !== "";
  const value = Number(score);
  const sound =
    !given ||
    (scale !== null &&
      Number.isFinite(value) &&
      value >= 0 &&
      value <= scale.max &&
      (scale.hasDecimals || Number.isInteger(value)));
  const canSubmit = tastedOn !== "" && sound && !busy;

  return (
    <form
      className="mt-2 grid gap-3 sm:grid-cols-[10rem_6rem_1fr]"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        void saveMyTastingNote(cellarId, bottle.id, {
          tastedOn,
          text,
          score: given ? value : null,
          isFaulty,
        })
          .then(onDone)
          .catch(() => setError(t("wine.note.saveFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <div>
        <label className={fieldLabel} htmlFor={`note-date-${bottle.id}`}>
          {t("wine.drunkOn")}
        </label>
        <input
          id={`note-date-${bottle.id}`}
          type="date"
          value={tastedOn}
          onChange={(event) => setTastedOn(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`note-score-${bottle.id}`}>
          {scale === null
            ? t("wine.note.score")
            : t("wine.ownRating", { max: scale.max })}
        </label>
        <input
          id={`note-score-${bottle.id}`}
          inputMode={scale?.hasDecimals === true ? "decimal" : "numeric"}
          value={score}
          onChange={(event) => setScore(event.target.value)}
          className={`${control} ${resting} font-display mt-1 text-center`}
        />
      </div>

      <div>
        <label className={fieldLabel} htmlFor={`note-text-${bottle.id}`}>
          {t("wine.tastingNote")}
        </label>
        <input
          id={`note-text-${bottle.id}`}
          value={text}
          onChange={(event) => setText(event.target.value)}
          className={`${control} ${resting} mt-1`}
        />
      </div>

      <label className="col-span-full flex items-baseline gap-2 text-sm">
        <input
          type="checkbox"
          checked={isFaulty}
          onChange={(event) => setIsFaulty(event.target.checked)}
        />
        {t("wine.faulty")}
      </label>

      <div className="col-span-full flex flex-wrap items-center gap-4">
        <button type="submit" disabled={!canSubmit} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.note.save")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
        >
          {t("wine.cancel")}
        </button>
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}
